import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Loader } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import ModelCatalogScope from '@/components/ModelCatalogScope';
import { summarizeModels } from '@/lib/modelFilters';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "AI Models" (`/app-provider-model`) — where model lists are refreshed, hidden and priced.
 * Nested under the Connections sidebar group because a model list belongs to a credential,
 * not to the platform.
 *
 * ONE credential's table at a time, chosen by the chip row. The first cut of this page
 * stacked every scope expanded, which on a real install meant 500+ rows and a page
 * thousands of pixels tall — a catalogue that grows with the provider must not grow the
 * page with it.
 *
 * A scope is one credential: each `ai-model` Connection, plus this app's shared .env key
 * per provider. The two are listed separately on purpose — the same provider can back both
 * at once, and two accounts can be entitled to different models, so merging them would
 * report one account's list as the other's.
 */
function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

function CredentialChips({ scopes, selectedKey, onSelect }) {
  // With one credential the chip row is a control with no choice to make.
  if (scopes.length < 2) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {scopes.map((s) => {
        const active = s.key === selectedKey;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onSelect(s.key)}
            className={`text-xs px-3 py-1.5 rounded-full border ${active ? 'bg-primary-600 text-white border-primary-600' : 'border-[var(--app-border)] text-secondary-500'}`}
          >
            {s.name}
            <span className={active ? 'ml-2 opacity-80' : 'ml-2 text-secondary-400'}>{s.models.length}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Totals across every credential, so the header answers "is anything wrong anywhere"
 *  without making you click through each one. */
function useTotals(scopes) {
  return useMemo(() => scopes.reduce((acc, s) => {
    const { total, shown, unpriced } = summarizeModels(s.models);
    return {
      total: acc.total + total, shown: acc.shown + shown, unpriced: acc.unpriced + unpriced,
    };
  }, { total: 0, shown: 0, unpriced: 0 }), [scopes]);
}

export default function ModelCatalogPage() {
  const { fetchApi } = useAppApi();
  const [scopes, setScopes] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [syncingAll, setSyncingAll] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('modelCatalogList')
      .then((res) => setScopes(res?.scopes || []))
      .catch((ex) => setErr(apiError(ex)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Keyed by scope.key, which survives a reload — holding the object would reset the
  // selection to the first credential every time a sync refreshed the list.
  const selected = scopes.find((s) => s.key === selectedKey) || scopes[0] || null;
  const totals = useTotals(scopes);

  const syncAll = async () => {
    setSyncingAll(true);
    setErr('');
    try {
      // Sequential, not Promise.all: each sync is a real provider call behind a per-user
      // rate limit, and firing a dozen at once would spend the whole allowance on one click.
      for (const scope of scopes.filter((s) => s.supported)) {
        await fetchApi('modelCatalogSync', {
          provider: scope.provider,
          connection_id: scope.connection_id || undefined,
        }).catch(() => {});
      }
      await reload();
    } finally {
      setSyncingAll(false);
    }
  };

  if (loading) return <PageShell><Loader text="Loading model catalogue…" /></PageShell>;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="AI Models"
        subtitle={(
          <>
            <p className="text-sm text-secondary-500">
              {scopes.length} credential(s) · {totals.total} model(s) · {totals.shown} shown — discovered
              from each provider, not hardcoded.
            </p>
          </>
        )}
        actions={(
          <Can module="connections" action="rotate" fallback={null}>
            <Button size="sm" variant="secondary" onClick={syncAll} disabled={syncingAll || !scopes.length}>
              {syncingAll ? 'Syncing…' : 'Sync all'}
            </Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Could not load">{err}</Alert>}

      {totals.unpriced > 0 && (
        <Alert variant="warning" title={`${totals.unpriced} shown model(s) have no price`}>
          An agent with a monthly budget pauses rather than spend against a cost it cannot measure.
          Filter to <strong>Unpriced only</strong> below and fill in <span className="font-mono">$/1M in</span>
          {' '}and <span className="font-mono">$/1M out</span> for any model you intend to put a budgeted
          agent on. OpenRouter publishes its own prices; the other providers do not.
        </Alert>
      )}

      {!scopes.length && (
        <Card className="p-3 space-y-2">
          <div className="text-sm font-semibold">No AI-model credentials yet</div>
          <p className="text-sm text-secondary-500">
            Add an <Link className="text-primary-600" to="/app-connection">AI Model connection</Link> for your
            company, or set a provider key in this app&apos;s .env — a credential is what a model list is
            discovered with.
          </p>
        </Card>
      )}

      <CredentialChips scopes={scopes} selectedKey={selected?.key} onSelect={setSelectedKey} />

      {selected && <ModelCatalogScope scope={selected} onChanged={reload} />}

      {!!scopes.length && (
        <p className="text-xs text-secondary-500">
          Hidden models are kept, not deleted — they just stay out of the model dropdown on an agent or
          workflow LLM node, and a name typed by hand via &quot;Custom…&quot; still works either way. Lists
          refresh by themselves once a day; Sync models is the button for &quot;a model launched this
          morning&quot;.
        </p>
      )}
    </PageShell>
  );
}
